import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { SwalService } from '../../../../common/services/swal.service';
import { BanksService } from '../../services/banks.service';
import { AuthService } from '../../../auth/services/auth.service';
import { BankSummaryDto } from '../../../../core/data/new-dtos/bank.dtos';
import { TableComponent } from '../../../../common/components/table/table.component';
import { TableColumnDirective } from '../../../../common/directives/table-column.directive';
import { TableContainerComponent } from '../../../../common/components/table-container/table-container.component';
import { SharedModule } from '../../../../common/shared.module';
import TableHeader from '../../../../common/interfaces/table/tableHeader';

@Component({
    selector: 'app-banks-list',
    standalone: true,
    imports: [
        CommonModule,
        SharedModule,
        TableComponent,
        TableColumnDirective,
        TableContainerComponent,
    ],
    templateUrl: './banks-list.component.html',
    styleUrls: ['./banks-list.component.scss'],
    providers: [SwalService],
})
export class BanksListComponent implements OnInit {
    banks: BankSummaryDto[] = [];
    filteredBanks: BankSummaryDto[] = [];
    searchTerm = '';

    tableHeaders: TableHeader[] = [
        { attribute: 'name', label: 'Nombre' },
        { attribute: 'actions', label: 'Acciones' },
    ];

    constructor(
        private swal: SwalService,
        private banksService: BanksService,
        private auth: AuthService,
        private router: Router,
    ) { }

    /** Creating and deleting banks stays with La Colonial. */
    get isSuperUser() {
        return this.auth.isSuperUser;
    }

    async ngOnInit() {
        await this.auth.profileReady();
        await this.refresh();
    }

    async refresh() {
        this.swal.showLoading('Cargando bancos...');
        try {
            const banks = await lastValueFrom(this.banksService.getBanks());
            // A global director administers only their own bank
            this.banks = this.auth.isGlobal
                ? banks.filter((b) => b.id === this.auth.bankProfile?.bankId)
                : banks;
            this.applyFilter();
        } catch (e) {
            this.banks = [];
            this.filteredBanks = [];
        } finally {
            this.swal.dismiss();
        }
    }

    onSearch(term: string) {
        this.searchTerm = term;
        this.applyFilter();
    }

    private applyFilter() {
        if (!this.searchTerm.trim()) {
            this.filteredBanks = [...this.banks];
        } else {
            const lowerTerm = this.searchTerm.toLowerCase();
            this.filteredBanks = this.banks.filter(bank =>
                bank.name?.toLowerCase().includes(lowerTerm)
            );
        }
    }

    navigateToCreate() {
        this.router.navigate(['/banks/new']);
    }

    navigateToEdit(id: number) {
        this.router.navigate(['/banks', id, 'edit']);
    }

    async deleteBank(id: number) {
        const { isConfirmed } = await this.swal.confirm(
            'Esta acción no se puede deshacer. Las sucursales asociadas también serán eliminadas.',
            '¿Eliminar banco?',
            { confirmButtonText: 'Sí, eliminar' }
        );

        if (!isConfirmed) return;

        this.swal.showLoading('Eliminando...');
        try {
            await lastValueFrom(this.banksService.deleteBank(id));
            this.banks = this.banks.filter((b) => b.id !== id);
            this.applyFilter();
            await this.swal.success('Banco eliminado.');
        } catch (e) {
            this.swal.error('No se pudo eliminar el banco.');
        } finally {
            this.swal.dismiss();
        }
    }
}

